import { FormEvent, useEffect, useRef, useState } from 'react'
import { DevConsoleOrderTrace, OrderTraceKey } from '@/console/api/types'
import { useOrderTrace } from '@/console/data/devConsoleOrders'
import { TABS } from '@/console/shell/tabs'
import { PanelState } from '@/console/state/panelState'
import { loadTraceKey, saveTraceKey } from '@/console/state/searchFormStorage'
import { orderStatusMeta } from '@/console/tabs/fopt/orderSearch'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { DataTable } from '@/console/ui/DataTable'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { PageHeading } from '@/console/ui/PageHeading'
import { ResultState } from '@/console/ui/ResultState'
import { RowList } from '@/console/ui/RowList'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Tag } from '@/console/ui/Tag'
import { TraceSectionList } from './TraceSectionList'
import { TraceSectionMap } from './TraceSectionMap'
import { bizestRemarkOf, bizestVerdictFallback, bizestVerdictMeta, traceSectionAnchor, validateTraceKey } from './traceView'
import styles from './trace.module.css'

const tab = TABS.find((t) => t.key === 'trace')!

const VERDICT_LEGEND = ['SENT', 'RESEND_PENDING', 'CANCEL_SKIPPED', 'NOT_SENT'] as const

/**
 * LOGISPRD-13035 — 주문 Trace 탭. refNo1 · 옵션번호 한 쌍으로 주문 · 비제스트 판정 · 15구간을 한 번에
 * 가져온다. F-OPT 조회 행의 "Trace →" 로 들어오면 `initialTarget` 이 채워져 마운트 시 한 번 조회한다
 * (StrictMode 이중 발화는 `hasAutoRunRef` 로 막는다 — `data/holds.ts` 선례).
 *
 * LOGISPRD-13034 — 새로고침해도 입력값이 남게 `sessionStorage` 에도 저장한다. `initialTarget`(F-OPT
 * 에서 넘어온 값)이 있으면 그쪽이 저장된 값보다 우선하고, 자동 조회 직전에 그 값을 세션에도 저장한다
 * — 그래야 새로고침 뒤에도 "방금 Trace 한 키"가 복원되지, 그보다 오래된 수동 입력값이 되살아나지
 * 않는다. 복원된 값만으로는 자동 조회하지 않는다 — 자동 1회 조회는 지금처럼 `initialTarget` 이
 * 있을 때만이다.
 */
export const TraceTab = ({ initialTarget }: { initialTarget: OrderTraceKey | null }): JSX.Element => {
  // lazy initializer — 그냥 쓰면 매 렌더(입력 한 글자마다)마다 sessionStorage 를 다시 읽고 파싱한다.
  const [key, setKey] = useState<OrderTraceKey>(() => initialTarget ?? loadTraceKey() ?? { refNo1: '', orderOptionNumber: '' })
  const { state, run } = useOrderTrace()
  const hasAutoRunRef = useRef(false)
  const invalid = validateTraceKey(key)

  const updateKey = (next: OrderTraceKey): void => {
    setKey(next)
    saveTraceKey(next)
  }

  useEffect(() => {
    if (hasAutoRunRef.current || !initialTarget || validateTraceKey(initialTarget)) return
    hasAutoRunRef.current = true
    saveTraceKey(initialTarget)
    run(initialTarget)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 진입 시 1회만
  }, [])

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    if (invalid || state.status === 'loading') return
    run(key)
  }

  const jump = (sectionKey: string): void => {
    const el = document.getElementById(traceSectionAnchor(sectionKey)) as HTMLDetailsElement | null
    if (!el) return
    el.open = true
    el.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  const data = state.status === 'ready' ? state.data : null
  // 공유 ResultState 의 idle 문구("조회 버튼을 누르면...")는 이 탭 버튼 이름(Trace)과 안 맞는다 —
  // ResultState 는 그대로 두고 idle 만 이 탭 문구의 empty 로 바꿔 전달한다.
  const ordersState: PanelState<DevConsoleOrderTrace> =
    state.status === 'idle' ? { status: 'empty', reason: '원주문번호와 옵션번호를 넣고 Trace 를 누르면 가져와요.' } : state

  return (
    <>
      <PageHeading title={tab.heading} sub={tab.sub} />

      <SectionHeading id="s-trace-query" level={2} title="조회" badge={<SourceBadge tone="new">신규 · GET /dev-console/orders/trace</SourceBadge>} />
      <form className={styles.query} onSubmit={submit}>
        <Field label="원주문번호 (refNo1)">
          <input value={key.refNo1} placeholder="202609151141320619" onChange={(e) => updateKey({ ...key, refNo1: e.target.value })} />
        </Field>
        <Field label="주문 옵션번호">
          <input value={key.orderOptionNumber} placeholder="487990013" onChange={(e) => updateKey({ ...key, orderOptionNumber: e.target.value })} />
        </Field>
        <Button type="submit" disabled={Boolean(invalid) || state.status === 'loading'}>
          Trace
        </Button>
      </form>
      <Note>두 값 모두 필요해요. F-OPT 주문 조회 결과의 Trace → 를 누르면 채워진 채로 넘어와요.</Note>

      <SectionHeading id="s-trace-orders" level={2} title="주문" count={data ? `${data.orders.length}건 · 분할 포함 · 최대 50건` : undefined} />
      {data?.ordersTruncated ? <Callout tone="warn">주문이 50건을 넘어 일부만 가져왔어요. 잘린 주문의 구간 행도 빠져 있어요.</Callout> : null}
      <ResultState state={ordersState}>
        {data && data.orders.length === 0 ? (
          <Callout tone="warn">이 refNo1 · 옵션번호로 standard_order 가 없어요. 조회 키를 만들 수 없는 구간은 건너뛰고, 나머지는 refNo1 로 찾았어요.</Callout>
        ) : (
          <DataTable
            columns={[
              { key: 'id', label: 'standard_order.id', mono: true },
              { key: 'no', label: '주문번호', mono: true },
              { key: 'shipper', label: '화주', mono: true },
              { key: 'address', label: 'address_id', mono: true },
              { key: 'status', label: '상태' },
              { key: 'qty', label: '수량', align: 'right' },
              { key: 'cut', label: '컷 id', align: 'right' },
              { key: 'consol', label: '합포번호', mono: true },
            ]}
            rows={(data?.orders ?? []).map((o) => {
              const s = orderStatusMeta(o.orderStatus)
              return {
                key: String(o.standardOrderId),
                cells: {
                  id: o.standardOrderId,
                  no: o.orderNumber ?? '—',
                  shipper: o.shipperCode ?? '—',
                  address: o.addressId ?? '—',
                  status: <Tag tone={s.tone}>{s.label}</Tag>,
                  qty: o.orderQuantity ?? '—',
                  cut: o.outboundCutSeqId ?? '—',
                  consol: o.orderConsolNumber ?? '—',
                },
              }
            })}
          />
        )}
      </ResultState>

      <section id="s-trace-bizest" aria-label="비제스트 출고실적 전송">
        <SectionHeading level={2} title="비제스트 출고실적 전송" count="IIF_BIZEST_GI 행마다 판정" />
        {data ? (
          // 판정 목록만 감싼다 — 아래 레전드에도 같은 칩 라벨이 있어 섹션 단위로 찾으면 두 번 잡힌다.
          <div data-testid="bizest-verdicts">
            <RowList
              rows={
                data.bizestOutboundResults.length === 0
                  ? (() => {
                      // 판정 목록이 비어도 그대로 "실적 미생성"이라 단정하지 않는다 — 9번 구간
                      // 조회가 실패해서 빈 것일 수 있다(bizestVerdictFallback 주석 참고).
                      const fallback = bizestVerdictFallback(data)
                      return [{ key: 'none', name: fallback.label, desc: fallback.desc, right: <Tag tone={fallback.tone}>{fallback.label}</Tag> }]
                    })()
                  : data.bizestOutboundResults.map((v, i) => {
                      const meta = bizestVerdictMeta(v.status)
                      const remark = bizestRemarkOf(data, v.id)
                      return {
                        key: String(v.id ?? `n${i}`),
                        name: `IIF_BIZEST_GI #${v.id ?? '—'}`,
                        mono: true,
                        desc: remark ? `${meta.why} · REMARK — ${remark}` : meta.why,
                        right: (
                          <>
                            <Button variant="ghost" onClick={() => jump('bizestOutboundResult')}>
                              원본 행 ↓
                            </Button>
                            <Tag tone={meta.tone}>{meta.label}</Tag>
                          </>
                        ),
                      }
                    })
              }
            />
          </div>
        ) : null}
        <div className={styles.bizLegend}>
          {VERDICT_LEGEND.map((s) => (
            <div key={s}>
              <Tag tone={bizestVerdictMeta(s).tone}>{bizestVerdictMeta(s).label}</Tag>
              {s}
            </div>
          ))}
        </div>
      </section>

      <SectionHeading id="s-trace-flow" level={2} title="구간 15개" count="주문 생애주기 순 · 칸을 누르면 그 구간으로" />
      {data ? (
        <>
          <TraceSectionMap sections={data.sections} onJump={jump} />
          <TraceSectionList sections={data.sections} />
          <Note>컬럼 이름은 테이블 원본 그대로예요. 구간당 최대 200행이에요.</Note>
        </>
      ) : null}
    </>
  )
}
